import { useState } from 'react';
import type { SliceDimension, SliceMetric } from '@death-star/shared';
import { SliceBarChart, type Bucket } from './SliceBarChart.js';
import { SliceTrendChart } from './SliceTrendChart.js';
import type { Annotation } from '../../lib/annotations.js';
import { PCT, SECS, isRatioMetric, isTimeMetric, pickFormatter } from './format.js';

// Re-exported for existing importers / tests that pull these from this module.
// Canonical definitions live in ./format.ts.
export { PCT, SECS, pickFormatter };

const DIM_LABELS: Record<SliceDimension, string> = {
  size_bucket: 'by size bucket',
  path_category: 'by path category',
  change_type: 'by change type',
  author_type: 'by author type',
  primary_language: 'by primary language',
};

export function dimensionLabel(d: SliceDimension): string {
  return DIM_LABELS[d];
}

export interface SliceTileProps {
  dimension: SliceDimension;
  metric: SliceMetric;
  buckets: Bucket[];
  trendDates: string[];
  trendSeries: Record<string, number[]>;
  note?: string;
  valueFormatter?: (v: number) => string;
  annotations?: Annotation[];
}

/**
 * A tile is "empty" when it has nothing meaningful to render.
 *
 * - For **rate metrics** (auto_approval, revert_rate, etc.): empty means
 *   `buckets.length === 0`. A bucket with `current=0` is real signal
 *   ("0% auto-approval in this bucket") and should render.
 * - For **time metrics** (ttm, ttfr): empty also includes the all-zero
 *   case. `current=0` for a time metric means "no PR in this bucket has
 *   `time_to_*_seconds` populated yet" — not a real measurement of 0
 *   seconds. Rendering it as a bar chart produces duplicate "0 m" Y-axis
 *   ticks because recharts auto-scales to domain `[0, 0]`.
 */
export function isTileEmpty(buckets: Bucket[], metric?: SliceMetric): boolean {
  if (buckets.length === 0) return true;
  if (metric && isTimeMetric(metric)) {
    return buckets.every((b) => b.current === 0);
  }
  return false;
}

export function SliceTile({
  dimension, metric, buckets, trendDates, trendSeries, note, valueFormatter, annotations,
}: SliceTileProps) {
  const [view, setView] = useState<'bars' | 'trend'>('bars');
  const fmt = pickFormatter(metric, valueFormatter);
  const isEmpty = isTileEmpty(buckets, metric);

  return (
    <div
      style={{
        background: 'var(--loop-neutral-100)',
        border: '1px solid var(--loop-neutral-300)',
        borderRadius: 'var(--loop-radius-md)',
        padding: 'var(--loop-space-150)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8, gap: 8 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: '0.875rem', fontWeight: 528, color: 'var(--loop-neutral-1100)' }}>
            {dimensionLabel(dimension)}
          </div>
          {note && (
            <div style={{ fontSize: '0.75rem', color: 'var(--loop-neutral-600)', marginTop: 2 }}>{note}</div>
          )}
        </div>
        <div className="filter-toggle" style={{ opacity: isEmpty ? 0.5 : 1, flexShrink: 0 }}>
          {(['bars', 'trend'] as const).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setView(v)}
              disabled={isEmpty}
              className={`filter-btn${view === v ? ' active' : ''}`}
              aria-pressed={view === v}
              style={{ padding: '4px 10px', fontSize: '0.75rem', cursor: isEmpty ? 'default' : 'pointer' }}
            >
              {v}
            </button>
          ))}
        </div>
      </div>
      {isEmpty
        ? <div style={{ height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--loop-neutral-500)', fontSize: '0.875rem' }}>No data in this window.</div>
        : view === 'bars'
          ? <SliceBarChart metric={metric} buckets={buckets} valueFormatter={fmt} />
          : <SliceTrendChart dates={trendDates} series={trendSeries} valueFormatter={fmt} isRate={!isTimeMetric(metric) && !isRatioMetric(metric)} annotations={annotations} />}
      {/* The bars can only carry one scalar (the high+critical share), so the
          composition — including how much of it is 'unspecified' — is rendered
          as text. Without this, a bucket that is 90% severity-less would be
          indistinguishable from one the council genuinely rated low. */}
      {metric === 'severity_mix' && !isEmpty && <SeverityMixLines buckets={buckets} />}
    </div>
  );
}

export function SeverityMixLines({ buckets }: { buckets: Bucket[] }) {
  const withMix = buckets.filter((b) => b.severity && Object.keys(b.severity).length > 0);
  if (withMix.length === 0) return null;
  return (
    <div style={{ marginTop: 8, fontSize: '0.7rem', color: 'var(--loop-neutral-700)', lineHeight: 1.6 }}>
      {withMix.map((b) => {
        const mix = b.severity!;
        const total = Object.values(mix).reduce((s, v) => s + v, 0);
        const parts = Object.entries(mix)
          .sort((x, y) => y[1] - x[1])
          .map(([sev, n]) => `${sev} ${PCT(total > 0 ? n / total : 0)}`)
          .join(' · ');
        return (
          <div key={b.name}>
            <strong>{b.name}</strong>{': '}{parts}
          </div>
        );
      })}
    </div>
  );
}
